// Lib
import React from 'react';
import ResizeObserver from 'resize-observer-polyfill';

/**
 * This hook ensures that the scrollable area remains scrolled to the bottom, if the child element
 *  or scrollable element changes size, or if the user starts dragging an item.
 */
const useIsOverflowingObserver = (
    scrollableElementRef: React.MutableRefObject<HTMLDivElement>,
    childElementRef: React.MutableRefObject<HTMLDivElement>,
) => {
    const [isOverflowing, setIsOverflowing] = React.useState(false);

    const updateOverflowState = React.useCallback(() => {
        if (!scrollableElementRef.current) return;

        const scrollHeight = scrollableElementRef.current.scrollHeight;
        const scrollableElementHeight = scrollableElementRef.current.clientHeight;
        const isCurrentlyOverflowing = scrollHeight > scrollableElementHeight;
        setIsOverflowing(isCurrentlyOverflowing);
    }, []);

    /**
     * On resize, if we are scrolled to the bottom, scroll to the bottom again.
     */
    const onResize = React.useCallback(() => {
        requestAnimationFrame(updateOverflowState);
    }, []);

    /**
     * On mount, set up the scroll event listener and child element resize observer.
     */
    React.useEffect(() => {
        if (!scrollableElementRef.current) return;

        requestAnimationFrame(updateOverflowState);

        const resizeObserver = new ResizeObserver(onResize);
        resizeObserver.observe(scrollableElementRef.current);
        childElementRef.current && resizeObserver.observe(childElementRef.current);

        return () => {
            resizeObserver?.disconnect();
        };
    }, []);

    return isOverflowing;
};

export default useIsOverflowingObserver;
